import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ban, Loader2, ShieldCheck, Undo2 } from "lucide-react";
import { Button } from "../ui/button";
import { useI18n } from "../../i18n";

/**
 * P2/P3 — blocked / owner-closed conversations with a one-tap undo.
 * Source of truth: GET /api/protection/conversations.
 */

const API = "/api";

export const BlockedConversationsSection = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/protection/conversations`);
      setRows(r.data?.conversations || []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unblock = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/protection/conversations/${id}/unblock`);
      toast.success(t("app.protection.unblocked_ok"));
      await load();
    } catch (_e) {
      toast.error(t("app.protection.error"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="settings-blocks-card">
      <h2 className="mb-1 text-lg font-semibold text-[#0B2545]">{t("app.protection.blocked_title")}</h2>
      <p className="mb-4 text-sm text-slate-600">{t("app.protection.blocked_desc")}</p>

      <p className="mb-4 flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-700">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1B84FF]" />
        {t("app.protection.scope_note")}
      </p>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="settings-blocks-loading">
          <Loader2 className="h-5 w-5 animate-spin text-[#1B84FF]" />
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-600"
           data-testid="settings-blocks-empty">
          {t("app.protection.blocked_empty")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200">
          {rows.map((c) => (
            <li key={c.conversation_id} className="flex flex-wrap items-center gap-3 p-3.5"
                data-testid={`blocked-row-${c.conversation_id}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-700">
                <Ban className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-[#0B2545]">
                  {c.profile_name || t("app.inbox.protection")}
                </p>
                <p className="text-xs text-slate-600">
                  {c.status === "blocked"
                    ? t("app.protection.blocked_badge")
                    : t("app.protection.closed_badge")}
                  {c.reason_code ? ` · ${t(`app.protection.reason.${c.reason_code}`)}` : ""}
                  {c.blocked_at ? ` · ${new Date(c.blocked_at).toLocaleDateString()}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <Link to={`/mensajes?c=${c.conversation_id}`}>
                  <Button variant="outline" size="sm" className="border-slate-300 text-[#0B2545]"
                          data-testid={`blocked-open-${c.conversation_id}`}>
                    {t("app.protection.open_conversation")}
                  </Button>
                </Link>
                {c.status === "blocked" && (
                  <Button size="sm" disabled={busyId === c.conversation_id}
                          onClick={() => unblock(c.conversation_id)}
                          className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]"
                          data-testid={`blocked-unblock-${c.conversation_id}`}>
                    {busyId === c.conversation_id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Undo2 className="mr-1.5 h-3.5 w-3.5" />}
                    {t("app.protection.unblock")}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default BlockedConversationsSection;
